import { useOnClickOutside } from '@/hooks';
import useScrollIntoView from '@/hooks/useScrollIntoView';
import DropdownButton, {
  DropdownButtonSize,
} from '@/storybook/components/FormCollection/components/DropdownButton/DropdownButton';
import DropdownLabel from '@/storybook/components/FormCollection/components/DropdownLabel/DropdownLabel';
import DropdownList from '@/storybook/components/FormCollection/components/DropdownList/DropdownList';
import DropdownRatingPanel from '@/storybook/components/FormCollection/components/DropdownRatingPanel/DropdownRatingPanel';
import DropdownValueWithRatingStars from '@/storybook/components/FormCollection/components/DropdownValueWithRatingStars/DropdownValueWithRatingStars';
import styles from '@/storybook/components/FormCollection/Input.module.css';
import { DropdownPosition, handleDropdownListPosition } from '@/storybook/components/FormCollection/utils/utils';
import Text from '@/storybook/components/Text/Text';
import cx from 'classnames';
import { ReactNode, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

export type SingleLevelSelectProps<T = number> = {
  'label'?: ReactNode;
  'value'?: T | null;
  'isLoading'?: boolean;
  'onChange': (selectedValue?: T) => void;
  'placeholder'?: string;
  'disabled'?: boolean;
  'className'?: string;
  'clearable'?: boolean;
  'comingSoon'?: boolean;
  'adobeEventTrackFunctions'?: () => void;
  'onDropdownToggle'?: (isOpen: boolean) => void;
  'alignment'?: 'vertical' | 'horizontal';
  'data-testid'?: string;
  'dropdownLabelTestId'?: string;
  'dropdownButtonTestId'?: string;
  'dropdownListTestId'?: string;
  'dropdownPosition'?: DropdownPosition;
  'size'?: keyof typeof DropdownButtonSize;
};

export default function SingleLevelSelect<T extends number>({
  label,
  value,
  isLoading,
  onChange,
  placeholder,
  disabled = false,
  className,
  clearable,
  comingSoon,
  adobeEventTrackFunctions,
  onDropdownToggle,
  alignment = 'vertical',
  'data-testid': dataTestId = 'singleLevelSelect',
  dropdownLabelTestId,
  dropdownButtonTestId,
  dropdownListTestId,
  dropdownPosition,
  size = 'L',
}: SingleLevelSelectProps<T>) {
  const [dropdownListPosition, setDropdownLitPosition] = useState<DropdownPosition>(dropdownPosition ?? 'down');
  const [isOpen, setIsOpen] = useState(false);
  const { t } = useTranslation();
  const dropdownRef = useOnClickOutside(() => setIsOpen(false));

  useScrollIntoView({
    elementRef: dropdownRef,
    isVisible: isOpen,
  });

  const handleToggleDropdown = useCallback(() => {
    if (disabled) return;
    setIsOpen(prev => !prev);
    onDropdownToggle?.(!isOpen);
    handleDropdownListPosition(dropdownPosition, dropdownRef, setDropdownLitPosition);
  }, [disabled, dropdownPosition, dropdownRef, isOpen, onDropdownToggle]);

  const handleClickOption = useCallback(
    (optionValue: T) => {
      if (disabled) return;
      onChange(optionValue);
      setIsOpen(prev => !prev);
    },
    [onChange, disabled],
  );

  const handleOnClearValue = useCallback(() => {
    if (disabled || !clearable) return;
    onChange(undefined);
  }, [disabled, clearable, onChange]);

  const hasValue = value != null && value > 0;

  return (
    <div
      ref={dropdownRef}
      className={cx(styles.inputContainer, className)}
      data-alignment={alignment}
      data-testid={dataTestId}
      onClick={adobeEventTrackFunctions}
    >
      <DropdownLabel label={label} tooltip={comingSoon} data-testid={dropdownLabelTestId} />
      <section className={styles.dropdownContainer}>
        <DropdownButton
          type="singleLevelSelect"
          size={size}
          placeholder={
            <div className={styles.value}>
              <Text className={styles.label} data-testid="dropdownRatingPanelLabel">
                {!hasValue && <span>{t('components.levelSelectDropdown.levelTopicPrefix')} </span>}
                <span>{placeholder}</span>
              </Text>
            </div>
          }
          active={isOpen}
          onClick={handleToggleDropdown}
          onClear={handleOnClearValue}
          clearable={clearable}
          disabled={comingSoon || disabled}
          data-testid={dropdownButtonTestId}
          numOfValue={value ?? 0}
        >
          <DropdownValueWithRatingStars
            value={[{ label: placeholder ?? '', value: placeholder ?? '', level: value ?? 0 }]}
          />
        </DropdownButton>
        <DropdownList
          isOpen={isOpen}
          isLoading={isLoading}
          disabled={disabled}
          position={dropdownListPosition}
          data-testid={dropdownListTestId}
          numberOfSearchResults={1}
          optionsCount={6}
        >
          <DropdownRatingPanel onClick={handleClickOption} data-testid="singleLevelSelectRatingPanel" />
        </DropdownList>
      </section>
    </div>
  );
}
